import {
    EInvoicePendingActions,
    isStuckAtMisa,
} from './einvoice-pending-actions'
import type { ReactNode } from 'react'
import type { EInvoice, EInvoiceResolutionSource } from '@/lib/api/einvoice-api'
import { useEInvoiceMisaState } from '@/hooks/use-einvoice-misa-state'
import {
    driftSentence,
    misaDraftSentence,
    waitingSentence,
} from '@/lib/einvoice-stuck-copy'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    invoice: EInvoice | null
    deliveredAt?: string
    source: EInvoiceResolutionSource
    /** Whether this person may act at MISA now (permission and a working account). */
    canMutate: boolean
    /** The review-and-publish action of the existing flow, with an optional label. */
    publishAction: (label?: string) => ReactNode
    /** Small links under the buttons and the "check with MISA" call behind them. */
    links?: (extra?: ReactNode) => ReactNode
    refreshNow?: () => Promise<EInvoice | undefined>
}

/** The one card for an order whose invoice needs a person. It says what is
 *  wrong in one sentence and offers at most three buttons; the closing buttons
 *  and their dialogs are the ones every pending order has. */
export function EInvoiceStuckCard({
    orderId,
    invoice,
    deliveredAt,
    source,
    canMutate,
    publishAction,
    links,
    refreshNow,
}: Props) {
    const unanswered = Boolean(invoice && isStuckAtMisa(invoice))
    const waiting = invoice?.status === 'publish_unknown' && !unanswered
    const misa = useEInvoiceMisaState(orderId, unanswered && canMutate)
    const frame = {
        orderId,
        deliveredAt,
        source,
        invoice,
        links: links?.(),
        refreshNow,
    }

    if (waiting && invoice)
        return (
            <EInvoicePendingActions
                {...frame}
                hideClosing
                sentence={waitingSentence(
                    invoice.publication_requested_at ?? invoice.updated_at,
                )}
            />
        )

    if (unanswered) {
        if (!canMutate || misa.isError)
            return (
                <EInvoicePendingActions
                    {...frame}
                    hideClosing
                    sentence={m.einvoice_pending_stuck()}
                />
            )
        if (!misa.data)
            return (
                <EInvoicePendingActions
                    {...frame}
                    hideClosing
                    sentence={m.einvoice_stuck_checking()}
                />
            )
        if (!misa.data.misa_draft_exists)
            return (
                <EInvoicePendingActions
                    {...frame}
                    closingFirst
                    sentence={m.einvoice_stuck_nothing()}
                    primary={publishAction(m.einvoice_stuck_republish_btn())}
                />
            )
        return (
            <EInvoicePendingActions
                {...frame}
                closingFirst
                sentence={misaDraftSentence(misa.data.misa_draft_created_at)}
                primary={publishAction(m.einvoice_stuck_republish_btn())}
            />
        )
    }

    if (invoice?.status === 'rejected')
        return (
            <EInvoicePendingActions
                {...frame}
                sentence={m.einvoice_stuck_tax_refused()}
            />
        )

    if (
        invoice?.status === 'failed' &&
        invoice.error_message === 'DRAFT_TOTAL_DRIFT'
    )
        return (
            <EInvoicePendingActions
                {...frame}
                // No draft was ever saved, so there is nothing to preview.
                links={null}
                sentence={driftSentence(invoice.draft_drift)}
                primary={publishAction()}
            />
        )

    return <EInvoicePendingActions {...frame} primary={publishAction()} />
}
